import is from '@sindresorhus/is';

import { COMMENT_ID_ATTRIBUTE, commentMarkSelector } from './anchor/grammar';
import { stripCommentMarks } from './strip-comment-marks';
import type { CommentAnchor } from './types';

const CONTEXT_LENGTH = 64;

export type MarkRange = {
    readonly start: number;
    readonly end: number;
};

type CommentMarkRange = MarkRange & {
    readonly commentId: string;
};

type TouchedRange = {
    readonly node: Text;
    readonly localStart: number;
    readonly localEnd: number;
};

type TextProjectionSegment = {
    readonly end: number;
    readonly node: Text;
    readonly start: number;
};

type TextProjection = {
    readonly segments: readonly TextProjectionSegment[];
    readonly text: string;
};

const STRUCTURAL_TAG_NAMES = new Set(['li', 'ol', 'p', 'ul']);

const FORMATTING_WHITESPACE_PATTERN = /^[\t\n\f\r ]*$/u;

const isFormattingWhitespace = (text: string): boolean => FORMATTING_WHITESPACE_PATTERN.test(text);

const isStructuralElement = (node: Node | null): boolean =>
    node instanceof HTMLElement && STRUCTURAL_TAG_NAMES.has(node.tagName.toLowerCase());

const isStructuralFormattingContext = (node: HTMLElement, root: HTMLElement): boolean =>
    node === root || isStructuralElement(node);

const closestNonFormattingSibling = (node: Node, direction: 'nextSibling' | 'previousSibling'): Node | null => {
    let sibling = node[direction];
    while (sibling instanceof Text && isFormattingWhitespace(sibling.data)) {
        sibling = sibling[direction];
    }
    return sibling;
};

const isIgnorableFormattingTextNode = (node: Text, root: HTMLElement): boolean => {
    if (!isFormattingWhitespace(node.data)) {
        return false;
    }

    const parent = node.parentElement;
    if (is.null(parent) || !isStructuralFormattingContext(parent, root)) {
        return false;
    }

    const previousSibling = closestNonFormattingSibling(node, 'previousSibling');
    const nextSibling = closestNonFormattingSibling(node, 'nextSibling');

    return (
        is.null(previousSibling) ||
        is.null(nextSibling) ||
        (isStructuralElement(previousSibling) && isStructuralElement(nextSibling))
    );
};

const createTextProjection = (root: HTMLElement): TextProjection => {
    const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const segments: TextProjectionSegment[] = [];
    let text = '';

    while (walker.nextNode() !== null) {
        const node = walker.currentNode;
        if (!(node instanceof Text) || isIgnorableFormattingTextNode(node, root)) {
            continue;
        }

        const start = text.length;
        text += node.data;
        segments.push({ end: text.length, node, start });
    }

    return { segments, text };
};

export const sectionTextFromElement = (root: HTMLElement): string => createTextProjection(root).text;

export const sectionTextFromHtml = (html: string): string => {
    const parser = new globalThis.DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild;

    return root instanceof HTMLElement ? sectionTextFromElement(root) : '';
};

export const createCommentAnchor = (text: string, start: number, end: number): CommentAnchor => ({
    quoteSelector: {
        exact: text.slice(start, end),
        prefix: text.slice(Math.max(0, start - CONTEXT_LENGTH), start) || null,
        suffix: text.slice(end, end + CONTEXT_LENGTH) || null,
    },
    positionSelector: { start, end },
    status: 'anchored',
});

export const anchorFromMarkedHtml = (markedHtml: string, commentId: string): CommentAnchor | null => {
    const parser = new globalThis.DOMParser();
    const doc = parser.parseFromString(`<div>${markedHtml}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    if (!(root instanceof HTMLElement)) return null;

    const projection = createTextProjection(root);
    const markSelector = commentMarkSelector(commentId);
    let start: number | null = null;
    let end: number | null = null;
    for (const segment of projection.segments) {
        if (!is.nullOrUndefined(segment.node.parentElement?.closest(markSelector))) {
            if (is.null(start)) start = segment.start;
            end = segment.end;
        }
    }

    if (is.null(start) || is.null(end) || end <= start) return null;

    return createCommentAnchor(projection.text, start, end);
};

const allIndexesOf = (haystack: string, needle: string): readonly number[] => {
    if (needle.length === 0) return [];
    const indexes: number[] = [];
    let from = 0;
    while (from <= haystack.length) {
        const found = haystack.indexOf(needle, from);
        if (found === -1) break;
        indexes.push(found);
        from = found + 1;
    }

    return indexes;
};

export type CommentAnchorResolution =
    | { readonly status: 'anchored'; readonly range: MarkRange }
    | { readonly status: 'ambiguous' | 'unresolved'; readonly range: null };

const UNRESOLVED: CommentAnchorResolution = { status: 'unresolved', range: null };
const AMBIGUOUS: CommentAnchorResolution = { status: 'ambiguous', range: null };

const anchoredAt = (start: number, length: number): CommentAnchorResolution => ({
    status: 'anchored',
    range: { start, end: start + length },
});

export const resolveCommentAnchor = (
    anchor: CommentAnchor | null | undefined,
    text: string,
): CommentAnchorResolution => {
    if (is.nullOrUndefined(anchor)) return UNRESOLVED;
    const exact = anchor.quoteSelector.exact;
    if (exact.trim().length === 0) return UNRESOLVED;

    const { start, end } = anchor.positionSelector;
    if (start < end && end <= text.length && text.slice(start, end) === exact) {
        return { status: 'anchored', range: { start, end } };
    }

    const indexes = allIndexesOf(text, exact);
    if (is.emptyArray(indexes)) return UNRESOLVED;
    if (indexes.length === 1) {
        const [onlyIndex] = indexes;
        return is.undefined(onlyIndex) ? UNRESOLVED : anchoredAt(onlyIndex, exact.length);
    }

    const contextualMatches = indexes.filter((index) => {
        const prefix = anchor.quoteSelector.prefix;
        const suffix = anchor.quoteSelector.suffix;
        const prefixMatches = is.nullOrUndefined(prefix) || text.slice(0, index).endsWith(prefix);
        const suffixMatches = is.nullOrUndefined(suffix) || text.slice(index + exact.length).startsWith(suffix);

        return prefixMatches && suffixMatches;
    });
    const [match, secondMatch] = contextualMatches;
    if (is.undefined(match) || !is.undefined(secondMatch)) return AMBIGUOUS;

    return anchoredAt(match, exact.length);
};

export const resolveCommentAnchorRange = (anchor: CommentAnchor | null | undefined, text: string): MarkRange | null =>
    resolveCommentAnchor(anchor, text).range;

const textLengthInSectionRange = (sectionRoot: HTMLElement, range: Range): number => {
    const projection = createTextProjection(sectionRoot);
    let length = 0;

    for (const segment of projection.segments) {
        if (!range.intersectsNode(segment.node)) continue;

        const localStart = segment.node === range.startContainer ? range.startOffset : 0;
        const localEnd = segment.node === range.endContainer ? range.endOffset : segment.node.data.length;
        length += Math.max(0, localEnd - localStart);
    }

    return length;
};

export const measureSectionRangeOffsets = (sectionRoot: HTMLElement, range: Range): MarkRange => {
    const measure = sectionRoot.ownerDocument.createRange();
    measure.selectNodeContents(sectionRoot);
    measure.setEnd(range.startContainer, range.startOffset);
    const start = textLengthInSectionRange(sectionRoot, measure);
    measure.setEnd(range.endContainer, range.endOffset);
    const end = textLengthInSectionRange(sectionRoot, measure);
    return { start, end };
};

const collectTouchedRanges = (root: HTMLElement, startChar: number, endChar: number): readonly TouchedRange[] => {
    const projection = createTextProjection(root);
    const touched: TouchedRange[] = [];
    for (const segment of projection.segments) {
        if (segment.end > startChar && segment.start < endChar) {
            touched.push({
                node: segment.node,
                localStart: Math.max(0, startChar - segment.start),
                localEnd: Math.min(segment.node.data.length, endChar - segment.start),
            });
        }
        if (segment.end >= endChar) break;
    }

    return touched;
};

const wrapTextNodeRange = (node: Text, start: number, end: number, commentId: string): void => {
    if (end <= start) return;
    const parent = node.parentNode;
    if (is.null(parent)) return;
    const doc = node.ownerDocument;
    const middle = start === 0 ? node : node.splitText(start);
    if (end - start < middle.data.length) {
        middle.splitText(end - start);
    }
    const mark = doc.createElement('mark');
    mark.setAttribute(COMMENT_ID_ATTRIBUTE, commentId);
    parent.insertBefore(mark, middle);
    mark.appendChild(middle);
};

const applyMarkToElement = (root: HTMLElement, start: number, end: number, commentId: string): void => {
    for (const { node, localStart, localEnd } of collectTouchedRanges(root, start, end)) {
        wrapTextNodeRange(node, localStart, localEnd, commentId);
    }
};

export const applySectionCommentRangesToHtml = (html: string, ranges: readonly CommentMarkRange[]): string => {
    const cleanHtml = stripCommentMarks(html);
    if (is.emptyArray(ranges)) return cleanHtml;

    const parser = new globalThis.DOMParser();
    const doc = parser.parseFromString(`<div>${cleanHtml}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    if (!(root instanceof HTMLElement)) return cleanHtml;

    for (const range of ranges.slice().sort((a, b) => a.start - b.start)) {
        const textLength = createTextProjection(root).text.length;
        if (range.start < range.end && range.end <= textLength) {
            applyMarkToElement(root, range.start, range.end, range.commentId);
        }
    }

    return root.innerHTML;
};
